import { useState } from 'react';
import type { FunctionComponent, ReactNode, SVGProps } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { Select } from 'antd';
import Breadcrumbs from '../../components/Breadcrumbs';
import {
  DirFieldIcon,
  DirCalendarIcon,
  FolderIcon,
  FileXlsxIcon,
  ChatIcon,
  RefreshIcon,
  DdAssigneeIcon,
  DdClockIcon,
  DdDetailsIcon,
  DownloadIcon,
  StakeholderIcon,
  TemplateDocIcon,
} from '../../assets/icons';

type IconType = FunctionComponent<SVGProps<SVGSVGElement>>;

const CARD = 'rounded-xl border border-[#e5e7eb] bg-white p-6 shadow-card';

function CardTitle({ Icon, children }: { Icon: IconType; children: ReactNode }) {
  return (
    <div className="flex items-center gap-2">
      <Icon className="size-5 text-brand" />
      <h2 className="text-lg font-bold text-ink">{children}</h2>
    </div>
  );
}

function Meta({ Icon, children }: { Icon: IconType; children: ReactNode }) {
  return (
    <span className="flex items-center gap-2 text-sm text-[#6b7280]">
      <Icon className="size-4" />
      {children}
    </span>
  );
}

const COMMENTS = [
  {
    time: '1 hour ago',
    text: 'Booth 12 visit completed. All banners are in place and the president has updated the app.',
  },
  {
    time: '2 hours ago',
    text: 'Coordinating with the logistics team for the remaining material distribution.',
  },
];

function Avatar({ className = '' }: { className?: string }) {
  return (
    <span
      className={[
        'flex size-8 shrink-0 items-center justify-center rounded-full border border-[#d1d5db] bg-[#e5e7eb] text-[#9ca3af]',
        className,
      ].join(' ')}
    >
      <StakeholderIcon className="size-4" />
    </span>
  );
}

export default function DailyDirectionStatusPage() {
  const { id } = useParams();
  const location = useLocation();
  const state = location.state as { title?: string; dirId?: string } | null;
  const title = state?.title ?? 'Visit Booth Presidents';
  const dirId = state?.dirId ?? `DIR-${id ?? '1024'}`;

  const [status, setStatus] = useState('pending');

  return (
    <div className="flex flex-col gap-8">
      {/* Breadcrumb */}
      <Breadcrumbs
        items={[{ label: 'Daily Directions', to: '/user/directions' }, { label: title }]}
      />

      {/* Header */}
      <div className={[CARD, 'flex flex-col gap-4'].join(' ')}>
        <div className="flex flex-wrap items-center gap-3">
          <span className="rounded-md bg-[#f3f4f6] px-2.5 py-1 text-xs font-semibold text-ink">
            {dirId}
          </span>
          <span className="rounded-md bg-[#fee2e2] px-2.5 py-1 text-xs font-bold uppercase text-[#b91c1c]">
            High Priority
          </span>
          <span className="inline-flex items-center gap-1.5 rounded-full bg-brand px-2.5 py-1 text-xs font-bold uppercase text-white">
            <span className="size-1.5 rounded-full bg-white" />
            Active
          </span>
        </div>
        <h1 className="text-3xl font-bold text-ink">{title}</h1>
        <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
          <Meta Icon={DirFieldIcon}>Field Operations</Meta>
          <Meta Icon={DdAssigneeIcon}>Admin User</Meta>
          <Meta Icon={DirCalendarIcon}>Oct 24, 2024</Meta>
          <Meta Icon={DdClockIcon}>2 hours ago</Meta>
        </div>
      </div>

      {/* Directive Details */}
      <div className={[CARD, 'flex flex-col gap-6'].join(' ')}>
        <CardTitle Icon={DdDetailsIcon}>Directive Details</CardTitle>

        <div className="flex flex-col gap-2">
          <span className="text-xs font-bold uppercase tracking-[0.6px] text-[#6b7280]">Objective</span>
          <div className="rounded-r-md border-l-4 border-brand bg-brand-shade px-5 py-4 text-sm leading-[22px] text-ink">
            Establish direct contact with all assigned Booth Presidents to ensure operational
            readiness for the upcoming rally. Verify current volunteer counts and distribute updated
            campaign material guidelines.
          </div>
        </div>

        <div className="flex flex-col gap-2">
          <span className="text-xs font-bold uppercase tracking-[0.6px] text-[#6b7280]">
            Description
          </span>
          <p className="text-sm leading-[22px] text-ink">
            Teams must physically visit each booth location. Ensure that the Booth President has
            downloaded the latest version of the Chunaav App and is familiar with the new reporting
            module.
          </p>
          <p className="text-sm leading-[22px] text-ink">Key talking points:</p>
          <ul className="list-disc pl-5 text-sm leading-[22px] text-ink">
            <li>Confirm receipt of physical banners (sent yesterday).</li>
            <li>Verify local voter registration targets for the week.</li>
            <li>Address any technical issues with the app login.</li>
          </ul>
        </div>
      </div>

      {/* Resources & Attachments */}
      <div className={[CARD, 'flex flex-col gap-6'].join(' ')}>
        <div className="flex items-center justify-between">
          <CardTitle Icon={FolderIcon}>Resources &amp; Attachments</CardTitle>
          <span className="rounded-md bg-[#f3f4f6] px-2 py-1 text-xs font-bold text-[#4b5563]">
            3 Files
          </span>
        </div>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          {[
            { name: 'Talking_Points.pdf', Icon: TemplateDocIcon, color: 'text-[#ef4444]' },
            { name: 'Booth_List_W4.xlsx', Icon: FileXlsxIcon, color: 'text-[#16a34a]' },
          ].map((file) => (
            <div
              key={file.name}
              className="overflow-hidden rounded-lg border border-[#e5e7eb] bg-[#f9fafb]"
            >
              <div className="flex h-24 items-center justify-center bg-[#f8fafc]">
                <file.Icon className={['size-12', file.color].join(' ')} />
              </div>
              <div className="flex items-center justify-between border-t border-[#e5e7eb] bg-white px-3 py-3">
                <span className="truncate text-sm font-medium text-ink">{file.name}</span>
                <button
                  type="button"
                  aria-label={`Download ${file.name}`}
                  className="text-[#64748b] transition-colors hover:text-brand"
                >
                  <DownloadIcon className="size-5" />
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Comments */}
      <div className={[CARD, 'flex flex-col gap-6'].join(' ')}>
        <CardTitle Icon={ChatIcon}>Comments</CardTitle>

        <div className="flex gap-4">
          <Avatar />
          <div className="flex flex-1 flex-col items-end gap-4">
            <textarea
              rows={3}
              placeholder="Write a comment..."
              className="w-full resize-none rounded-lg border border-[#d1d5db] bg-white p-3 text-sm text-ink placeholder:text-[#6b7280] focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/15"
            />
            <button
              type="button"
              className="rounded-md bg-brand px-6 py-2 text-sm font-medium text-white shadow-btn transition-colors hover:bg-brand-dark"
            >
              Post Comment
            </button>
          </div>
        </div>

        <div className="flex flex-col gap-4 border-t border-[#f3f4f6] pt-4">
          {COMMENTS.map((c) => (
            <div key={c.time} className="flex gap-4">
              <Avatar />
              <div className="flex flex-col gap-1">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-semibold text-ink">User</span>
                  <span className="text-xs text-[#6b7280]">{c.time}</span>
                </div>
                <p className="text-sm text-[#374151]">{c.text}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Update Status */}
      <div className={[CARD, 'flex flex-col gap-6'].join(' ')}>
        <CardTitle Icon={RefreshIcon}>Update Status</CardTitle>
        <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
          <div className="flex flex-col gap-1.5">
            <span className="text-sm text-ink">Select new status</span>
            <Select
              className="w-full sm:w-[320px]"
              value={status}
              onChange={setStatus}
              options={[
                { value: 'pending', label: 'Pending' },
                { value: 'inprogress', label: 'In Progress' },
                { value: 'completed', label: 'Completed' },
                { value: 'overdue', label: 'Overdue' },
              ]}
            />
          </div>
          <button
            type="button"
            className="rounded-lg bg-brand px-8 py-2 text-sm font-medium text-white shadow-btn transition-colors hover:bg-brand-dark"
          >
            Submit
          </button>
        </div>
      </div>
    </div>
  );
}
